import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Loader2, ShieldCheck, Building2, MapPin, Mail, ArrowRight, CheckCircle2,
  AlertTriangle, XCircle, Clock, Lock,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useTranslation, Trans } from "react-i18next";
import { setLanguage } from "@/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const FONT_HEAD = '"Satoshi",sans-serif';
const C = {
  navy: "#0E141B", surface: "#161D25", border: "#303A44",
  gold: "#D89A00", goldHover: "#E7B515", goldPressed: "#8C5A00",
  cream: "#F7F5F0", ink: "#2C2E33", slate: "#777F88", grey: "#A6ADB5", line: "#E7E1D6",
};

const inputCls = "mt-1.5 w-full h-[48px] rounded-[12px] border bg-white px-4 text-[14px] outline-none transition-colors";
const labelCls = "block text-[11px] font-semibold uppercase tracking-[0.08em]";

const ERROR_ICON = { invalid: XCircle, expired: Clock, revoked: AlertTriangle, claimed: CheckCircle2, generic: AlertTriangle };

function Shell({ children }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 py-10" style={{ background: C.navy, fontFamily: FONT_HEAD }} data-testid="claim-page">
      <div className="absolute right-5 top-5"><LanguageSwitcher variant="light" persist={false} /></div>
      <img src="/fundio-logo-white.png" alt="FUNDIO" className="h-[46px] w-auto" data-testid="claim-logo" />
      <div className="mt-8 w-full max-w-[460px]">{children}</div>
      <p className="mt-8 flex items-center gap-1.5 text-[12px]" style={{ color: C.grey }}>
        <ShieldCheck size={13} style={{ color: C.gold }} /> {t("claim.footer")}
      </p>
    </div>
  );
}

function Card({ children }) {
  return <div className="rounded-2xl bg-white p-7 shadow-2xl" style={{ border: `1px solid ${C.line}` }}>{children}</div>;
}

export default function Claim() {
  const { token } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | error | start | setup | done
  const [errKind, setErrKind] = useState("generic");
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", confirm: "", terms: false, privacy: false, newsletter: false });
  const [formErr, setFormErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/claim/${token}`).then(({ data }) => {
      if (!alive) return;
      if (data.language) setLanguage(data.language);
      setInfo(data);
      if (data.claimable) { setState("start"); return; }
      if (data.status === "CLAIMED") { setErrKind("claimed"); setState("error"); return; }
      if (data.status === "REVOKED") { setErrKind("revoked"); setState("error"); return; }
      if (data.expired || data.status === "EXPIRED") { setErrKind("expired"); setState("error"); return; }
      setErrKind("generic"); setState("error");
    }).catch((e) => {
      if (!alive) return;
      setErrKind(e.response?.status === 404 ? "invalid" : "generic");
      setState("error");
    });
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setFormErr("");
    if (!form.email.trim()) return setFormErr(t("claim.err_email_required"));
    if (form.password.length < 8) return setFormErr(t("claim.err_pw_min"));
    if (!/[a-zA-Z]/.test(form.password) || !/\d/.test(form.password)) return setFormErr(t("claim.err_pw_rule"));
    if (form.password !== form.confirm) return setFormErr(t("claim.err_pw_mismatch"));
    if (!form.terms || !form.privacy) return setFormErr(t("claim.err_accept"));
    setSubmitting(true);
    try {
      await api.post(`/claim/${token}/complete`, {
        email: form.email.trim(), password: form.password, accept_terms: true,
      });
      setState("done");
    } catch (e2) {
      const s = e2.response?.status;
      if (s === 403) setFormErr(t("claim.err_email_mismatch"));
      else if (s === 410) { setErrKind("expired"); setState("error"); }
      else if (s === 409) { setErrKind("claimed"); setState("error"); }
      else setFormErr(formatApiError(e2.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  if (state === "loading") {
    return <Shell><Card><div className="flex justify-center py-10" data-testid="claim-loading"><Loader2 className="animate-spin" style={{ color: C.gold }} /></div></Card></Shell>;
  }

  if (state === "error") {
    const Icon = ERROR_ICON[errKind] || ERROR_ICON.generic;
    return (
      <Shell>
        <Card>
          <div className="text-center" data-testid="claim-error">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full" style={{ background: "rgba(216,154,0,.12)" }}>
              <Icon size={26} style={{ color: C.gold }} />
            </span>
            <h1 className="mt-4 text-[22px] font-black" style={{ color: C.ink }} data-testid="claim-error-title">{t(`claim.e_${errKind}_title`)}</h1>
            <p className="mt-2 text-[14px] leading-relaxed" style={{ color: C.slate }}>{t(`claim.e_${errKind}_body`)}</p>
            <button onClick={() => navigate("/login")} data-testid="claim-error-login"
              className="mt-6 inline-flex items-center gap-2 rounded-[12px] px-5 py-3 text-[14px] font-bold text-white" style={{ background: C.gold }}>
              {t("claim.to_login")} <ArrowRight size={16} />
            </button>
          </div>
        </Card>
      </Shell>
    );
  }

  if (state === "done") {
    return (
      <Shell>
        <Card>
          <div className="text-center" data-testid="claim-success">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full" style={{ background: "rgba(30,126,69,.12)" }}>
              <CheckCircle2 size={28} style={{ color: "#1E7E45" }} />
            </span>
            <h1 className="mt-4 text-[26px] font-black" style={{ color: C.ink }}>{t("claim.success_title")}</h1>
            <p className="mt-2 text-[14px] leading-relaxed" style={{ color: C.slate }}>
              <Trans i18nKey="claim.success_body" values={{ org: info?.organization_name }} components={{ b: <span className="font-semibold" style={{ color: C.ink }} /> }} />
            </p>
            <button onClick={() => navigate("/login")} data-testid="claim-goto-cockpit"
              className="mt-6 inline-flex items-center gap-2 rounded-[12px] px-5 py-3 text-[14px] font-bold text-white" style={{ background: C.gold }}>
              {t("claim.goto_cockpit")} <ArrowRight size={16} />
            </button>
          </div>
        </Card>
      </Shell>
    );
  }

  // start + setup share the org header
  const OrgHeader = () => (
    <div className="rounded-xl p-4" style={{ background: "#FBF9F4", border: `1px solid ${C.line}` }}>
      <div className="flex items-center gap-2.5">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ background: "rgba(216,154,0,.12)" }}><Building2 size={18} style={{ color: C.gold }} /></span>
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: C.slate }}>{t("claim.org_label")}</p>
          <p className="truncate text-[16px] font-black" style={{ color: C.ink }} data-testid="claim-org-name">{info?.organization_name}</p>
        </div>
      </div>
      {info?.locations?.length > 0 && (
        <div className="mt-3 space-y-1">
          {info.locations.map((l, i) => (
            <p key={i} className="flex items-center gap-1.5 text-[13px]" style={{ color: C.slate }} data-testid="claim-location">
              <MapPin size={13} style={{ color: C.gold }} /> {l.name}{l.city ? ` · ${l.city}` : ""}
            </p>
          ))}
        </div>
      )}
      <p className="mt-3 flex items-center gap-1.5 text-[13px]" style={{ color: C.slate }}>
        <Mail size={13} style={{ color: C.gold }} /> <span data-testid="claim-email-masked">{info?.email_masked}</span>
      </p>
    </div>
  );

  if (state === "start") {
    return (
      <Shell>
        <Card>
          <div data-testid="claim-start">
            <p style={{ color: C.goldHover }} className="text-[12px] font-bold uppercase tracking-[0.18em]">{t("claim.brand_eyebrow")}</p>
            <h1 className="mt-1.5 text-[26px] font-black leading-tight" style={{ color: C.ink }}>{t("claim.start_title")}</h1>
            <p className="mt-2 text-[14px] leading-relaxed" style={{ color: C.slate }}>
              {t("claim.start_hint")}
            </p>
            <div className="mt-5"><OrgHeader /></div>
            <button onClick={() => setState("setup")} data-testid="claim-start-cta"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-[12px] py-3.5 text-[14px] font-bold text-white transition-colors" style={{ background: C.gold }}>
              {t("claim.cta")} <ArrowRight size={16} />
            </button>
          </div>
        </Card>
      </Shell>
    );
  }

  // setup
  return (
    <Shell>
      <Card>
        <div data-testid="claim-setup">
          <p style={{ color: C.goldHover }} className="text-[12px] font-bold uppercase tracking-[0.18em]">{t("claim.setup_eyebrow")}</p>
          <h1 className="mt-1.5 text-[24px] font-black leading-tight" style={{ color: C.ink }}>{t("claim.setup_title")}</h1>
          <div className="mt-4"><OrgHeader /></div>

          <form onSubmit={submit} className="mt-5 space-y-4">
            <div>
              <label className={labelCls} style={{ color: C.slate }}>{t("claim.email_label")}</label>
              <input data-testid="claim-email-input" type="email" value={form.email} autoComplete="email"
                onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} style={{ borderColor: C.line, color: C.ink }}
                placeholder={t("claim.email_ph")} />
            </div>
            <div>
              <label className={labelCls} style={{ color: C.slate }}>{t("claim.password_label")}</label>
              <input data-testid="claim-password-input" type="password" value={form.password} autoComplete="new-password"
                onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} style={{ borderColor: C.line, color: C.ink }}
                placeholder={t("claim.password_ph")} />
            </div>
            <div>
              <label className={labelCls} style={{ color: C.slate }}>{t("claim.confirm_label")}</label>
              <input data-testid="claim-password-confirm" type="password" value={form.confirm} autoComplete="new-password"
                onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} style={{ borderColor: C.line, color: C.ink }}
                placeholder={t("claim.confirm_ph")} />
            </div>

            <label className="flex items-start gap-2.5 text-[13px]" style={{ color: C.ink }}>
              <input data-testid="claim-accept-terms" type="checkbox" checked={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.checked })} className="mt-0.5 h-4 w-4 accent-[#D89A00]" />
              <span><Trans i18nKey="claim.accept_terms" components={{ b: <span className="font-semibold" /> }} /></span>
            </label>
            <label className="flex items-start gap-2.5 text-[13px]" style={{ color: C.ink }}>
              <input data-testid="claim-accept-privacy" type="checkbox" checked={form.privacy} onChange={(e) => setForm({ ...form, privacy: e.target.checked })} className="mt-0.5 h-4 w-4 accent-[#D89A00]" />
              <span><Trans i18nKey="claim.accept_privacy" components={{ b: <span className="font-semibold" /> }} /></span>
            </label>
            <label className="flex items-start gap-2.5 text-[13px]" style={{ color: C.slate }}>
              <input data-testid="claim-newsletter" type="checkbox" checked={form.newsletter} onChange={(e) => setForm({ ...form, newsletter: e.target.checked })} className="mt-0.5 h-4 w-4 accent-[#D89A00]" />
              <span>{t("claim.newsletter")}</span>
            </label>

            {formErr && <p className="flex items-center gap-1.5 text-[13px] font-medium" style={{ color: "#DC2626" }} data-testid="claim-form-error"><AlertTriangle size={14} /> {formErr}</p>}

            <button type="submit" disabled={submitting} data-testid="claim-submit"
              className="flex w-full items-center justify-center gap-2 rounded-[12px] py-3.5 text-[14px] font-bold text-white transition-colors disabled:opacity-60" style={{ background: C.gold }}>
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />} {t("claim.submit")}
            </button>
          </form>
        </div>
      </Card>
    </Shell>
  );
}
